import { config } from "../../../base/constant";

export interface SalesforceLogPayload {
    api_name: string;
    endpoint_url: string;
    method?: string;
    user_id?: string | number | null;
    user_name?: string | null;
    user_phone?: string | null;
    user_email?: string | null;
    user_type?: string;
    request_payload?: any;
    response_status: 'SUCCESS' | 'FAILED' | 'ERROR';
    status_code?: number | null;
    response_summary?: string;
    response_data?: any;
    error_message?: string;
    response_time_ms?: number | null;
}

/**
 * Extracts available user details from localStorage safely
 */
export const getStoredCustomerInfo = () => {
    try {
        const storedOpportunities = localStorage.getItem("opportunitiesList");
        let oppData: any = null;
        if (storedOpportunities) {
            try {
                const parsed = JSON.parse(storedOpportunities);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    oppData = parsed[0];
                }
            } catch (e) {
                // ignore
            }
        }

        const phone = localStorage.getItem("customer_phone") || 
                      localStorage.getItem("mobile_number") || 
                      oppData?.customer_phone || 
                      oppData?.telephone_number_1 || 
                      "";

        const email = localStorage.getItem("customer_email") || 
                      localStorage.getItem("email") || 
                      oppData?.customer_email || 
                      oppData?.email_1 || 
                      "";

        const name = localStorage.getItem("customer_name") || 
                     oppData?.customer_name || 
                     oppData?.name || 
                     "";

        const customerId = localStorage.getItem("customer_id") || 
                           oppData?.id || 
                           "";

        return {
            id: customerId,
            name: name,
            phone: phone,
            email: email,
            customer_type: localStorage.getItem("customer_type") || "CUSTOMER"
        };
    } catch (err) {
        return {
            id: "",
            name: "",
            phone: "",
            email: "",
            customer_type: "CUSTOMER"
        };
    }
};

/**
 * Maps endpoint URL / method to a standard API name
 */
export const detectSalesforceApiName = (url: string): string => {
    const cleanUrl = url.toLowerCase();
    if (cleanUrl.includes("documentdownload")) return "DOCUMENT_DOWNLOAD";
    if (cleanUrl.includes("customerdetails")) return "CUSTOMER_DETAILS";
    if (cleanUrl.includes("notifications")) return "NOTIFICATIONS";
    if (cleanUrl.includes("easebuzz/payment")) return "EASEBUZZ_PAYMENT";
    if (cleanUrl.includes("progressdocuments")) return "PROGRESS_DOCUMENTS";
    if (cleanUrl.includes("progressinventory")) return "PROGRESS_INVENTORY";
    return "OTHER";
};

/**
 * Fire-and-forget logger function that sends Salesforce API Audit entry to backend
 */
export const recordSalesforceApiAudit = async (payload: SalesforceLogPayload): Promise<void> => {
    try {
        const baseUrl = (config().BASE_URL || "http://127.0.0.1:8000/api").replace(/\/+$/, "");
        const logEndpoint = `${baseUrl}/salesforce-api-logs/log/`;

        const user = getStoredCustomerInfo();

        const requestBody = {
            api_name: payload.api_name || detectSalesforceApiName(payload.endpoint_url),
            endpoint_url: payload.endpoint_url,
            method: payload.method || "POST",
            user_id: payload.user_id || user.id || null,
            user_name: payload.user_name || user.name || null,
            user_phone: payload.user_phone || user.phone || null,
            user_email: payload.user_email || user.email || null,
            user_type: payload.user_type || user.customer_type || "CUSTOMER",
            request_payload: payload.request_payload || {},
            response_status: payload.response_status,
            status_code: payload.status_code || (payload.response_status === "SUCCESS" ? 200 : 500),
            response_summary: payload.response_summary || "",
            response_data: payload.response_data || null,
            error_message: payload.error_message || "",
            response_time_ms: payload.response_time_ms !== undefined ? payload.response_time_ms : null,
        };

        // Non-blocking fetch
        fetch(logEndpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
        }).catch((err) => {
            console.warn("Could not record Salesforce API audit log:", err);
        });
    } catch (e) {
        console.warn("Salesforce API logging failed silently:", e);
    }
};
